import React, { useEffect, useState } from 'react';
import {
  Grid, Card, TextField, Input, Dialog, DialogActions, DialogContent,
  DialogTitle, Box, IconButton, FormControl, InputLabel, Switch, FormControlLabel,
  Select, MenuItem, Chip, Tooltip, Typography, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
} from '@mui/material';
import {
  Add, Delete, Edit, ArrowBack, AttachMoney, Visibility,
  Image as ImageIcon, Close, Download, InsertDriveFile,
  FormatListBulleted, Policy as PolicyIcon, QuestionAnswer,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import { Axios } from 'services/axios';
import { Loading, Notify } from 'notiflix';
import { useNavigate, useParams } from 'react-router-dom';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const LICENSING_TYPES = ['standard', 'commercial', 'exclusive'];

// ─── SafeArray helper — fixes "x.map is not a function" for JSON-encoded DB columns ──
// previewImages, editableFileUrls, formats may arrive as JSON strings from old seeded rows
const safeArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
};

// ─── Default thumbnail placeholder for features ──────────────────────────────
const FeatureThumbnailPlaceholder = ({ size = 80 }) => (
  <Box sx={{
    width: size, height: size, borderRadius: 2,
    bgcolor: 'grey.100', border: '1.5px dashed', borderColor: 'grey.300',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 0.25, flexShrink: 0,
  }}>
    <FormatListBulleted sx={{ fontSize: size * 0.35, color: 'grey.350' }} />
  </Box>
);

const CategoryDesigns = () => {
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');
  const [category, setCategory] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [countries, setCountries] = useState([]);

  const [openDesignDialog, setOpenDesignDialog] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState([]);

  const [keptPreviewImages, setKeptPreviewImages] = useState([]);
  const [keptEditableFiles, setKeptEditableFiles] = useState([]);

  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDesign, setViewingDesign] = useState(null);
  const [viewPricings, setViewPricings] = useState([]);
  const [viewFeatures, setViewFeatures] = useState([]);

  const [openPricingDialog, setOpenPricingDialog] = useState(false);
  const [pricingDesign, setPricingDesign] = useState(null);
  const [pricings, setPricings] = useState([]);
  const [selectedPricing, setSelectedPricing] = useState(null);

  const [openFeaturesDialog, setOpenFeaturesDialog] = useState(false);
  const [featuresDesign, setFeaturesDesign] = useState(null);
  const [features, setFeatures] = useState([]);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureThumbnailFile, setFeatureThumbnailFile] = useState(null);
  const [featuresOpenedFromView, setFeaturesOpenedFromView] = useState(false);

  const { register: registerFeature, handleSubmit: handleSubmitFeature, reset: resetFeature, setValue: setValueFeature } = useForm();

  const { categoryId } = useParams();
  const navigate = useNavigate();

  const { register: registerDesign, handleSubmit: handleSubmitDesign, reset: resetDesign, setValue: setValueDesign, formState: { errors: designErrors } } = useForm();
  const { register: registerPricing, handleSubmit: handleSubmitPricing, reset: resetPricing, setValue: setValuePricing, formState: { errors: pricingErrors } } = useForm();

  const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const { register: registerPolicy, handleSubmit: handleSubmitPolicy, reset: resetPolicy, setValue: setValuePolicy } = useForm();

  const [openFaqDialog, setOpenFaqDialog] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const { register: registerFaq, handleSubmit: handleSubmitFaq, reset: resetFaq, setValue: setValueFaq } = useForm();

  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [selectedFaqIds, setSelectedFaqIds] = useState([]);

  // ===== FETCH =====
  const fetchCategoryData = async () => {
    try {
      Loading.circle('Loading...');
      const response = await Axios.fetch(`design/admin/categories/${categoryId}`);
      const data = response.data.data;
      setCategory(data);
      setDesigns(data.designs || []);
    } catch {
      Notify.failure('Failed to load category');
      navigate('/admin/designs');
    } finally {
      Loading.remove();
    }
  };

  const fetchAttributeTypes = async () => {
    try {
      const response = await Axios.fetch(`design/admin/categories/${categoryId}/attribute-types`);
      setAttributeTypes(response.data.data || []);
    } catch { console.error('Failed to load attribute types'); }
  };

  const fetchCountries = async () => {
    try {
      const response = await Axios.fetch('countries');
      const data = response.data.data;
      setCountries(data?.countries || (Array.isArray(data) ? data : []));
    } catch { console.error('Failed to load countries'); }
  };

  const fetchPolicies = async () => {
    try {
      const res = await Axios.fetch(`design/policies?categoryId=${categoryId}`);
      setPolicies(res.data.data || []);
    } catch { console.error('Failed to load policies'); }
  };

  const fetchFaqs = async () => {
    try {
      const res = await Axios.fetch(`design/faqs?categoryId=${categoryId}`);
      setFaqs(res.data.data || []);
    } catch { console.error('Failed to load faqs'); }
  };

  const fetchPricings = async (designId) => {
    try {
      const response = await Axios.fetch(`design/design-items/${designId}/pricings`);
      return response.data.data || [];
    } catch { Notify.failure('Failed to load pricings'); return []; }
  };

  const handleDownloadEditableFile = async (designId, fileIndex, fileName) => {
    try {
      Loading.circle('Preparing download...');
      const response = await Axios.fetch(`design/admin/design-items/${designId}/editable-files/${fileIndex}/download`);
      const { url } = response.data.data;
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.target = '_blank'; a.rel = 'noopener noreferrer';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { Notify.failure('Failed to generate download link'); }
    finally { Loading.remove(); }
  };

  const getFileExt = (fileName) => { if (!fileName) return ''; return fileName.split('.').pop().toLowerCase(); };
  const getFileColor = (ext) => {
    const map = { pdf: '#e53935', svg: '#7b1fa2', ai: '#ff6d00', psd: '#1565c0', fig: '#1de9b6', zip: '#795548', jpg: '#43a047', jpeg: '#43a047', png: '#43a047', webp: '#43a047' };
    return map[ext] || '#607d8b';
  };

  // ===== ATTRIBUTE HELPERS =====
  const handleAttributeChange = (attributeTypeId, value, attrType) => {
    if (attrType.type === 'multi_enum') {
      const exists = selectedAttributes.find(a => a.attributeTypeId === attributeTypeId && a.value === value);
      if (exists) { setSelectedAttributes(prev => prev.filter(a => !(a.attributeTypeId === attributeTypeId && a.value === value))); }
      else { setSelectedAttributes(prev => [...prev, { attributeTypeId, value }]); }
    } else {
      setSelectedAttributes(prev => {
        const filtered = prev.filter(a => a.attributeTypeId !== attributeTypeId);
        if (value) return [...filtered, { attributeTypeId, value }];
        return filtered;
      });
    }
  };

  const isAttrValueSelected = (attributeTypeId, value) => selectedAttributes.some(a => a.attributeTypeId === attributeTypeId && a.value === value);
  const getSelectedValueForAttrType = (attributeTypeId) => selectedAttributes.find(a => a.attributeTypeId === attributeTypeId)?.value || '';
  const prefillAttributes = (design) => {
    if (!design.attributes) return;
    setSelectedAttributes(design.attributes.map(a => ({ attributeTypeId: a.attributeTypeId, value: a.value })));
  };

  // ===== DESIGN OPERATIONS =====
  const handleDesignSubmit = async (data) => {
    Loading.circle('Processing...');
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('slug', data.slug);
    formData.append('categoryId', categoryId);
    formData.append('basePrice', data.basePrice);
    formData.append('shortDescription', data.shortDescription || '');
    formData.append('longDescription', data.longDescription || '');
    if (data.licensingType) formData.append('licensingType', data.licensingType);
    if (selectedAttributes.length > 0) formData.append('attributes', JSON.stringify(selectedAttributes));
    if (data.thumbnail?.[0]) formData.append('thumbnail', data.thumbnail[0]);
    if (selectedDesign) formData.append('keptPreviewImages', JSON.stringify(keptPreviewImages));
    if (data.previewImages?.length > 0) { for (let i = 0; i < data.previewImages.length; i++) formData.append('previewImages', data.previewImages[i]); }
    if (selectedDesign) formData.append('keptEditableFiles', JSON.stringify(keptEditableFiles));
    if (data.editableFiles?.length > 0) { for (let i = 0; i < data.editableFiles.length; i++) formData.append('editableFiles', data.editableFiles[i]); }
    if (selectedPolicyId) formData.append('policyId', selectedPolicyId);
    else formData.append('policyId', '');

    const url = selectedDesign ? `design/design-items/${selectedDesign.id}` : 'design/design-items';
    try {
      let savedId = selectedDesign?.id;
      if (selectedDesign) {
        await Axios.put(url, formData, { 'Content-Type': 'multipart/form-data' });
      } else {
        const res = await Axios.post(url, formData, { 'Content-Type': 'multipart/form-data' });
        savedId = res.data?.data?.id;
        Notify.success(res.data?.message || 'Design created!');
      }
      if (savedId) await Axios.put(`design/design-items/${savedId}/faqs`, { faqIds: selectedFaqIds });
      if (selectedDesign) Notify.success('Design updated!');
      closeDesignDialog();
      fetchCategoryData();
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to save design');
    } finally { Loading.remove(); }
  };

  const handleEditDesign = async (designId) => {
    const baseDesign = designs.find(d => d.id === designId);
    if (!baseDesign) return;
    setSelectedDesign(baseDesign);
    setValueDesign('title', baseDesign.title);
    setValueDesign('slug', baseDesign.slug);
    setValueDesign('basePrice', baseDesign.basePrice);
    setValueDesign('shortDescription', baseDesign.shortDescription || '');
    setValueDesign('longDescription', baseDesign.longDescription || '');
    setValueDesign('licensingType', baseDesign.licensingType || 'standard');
    prefillAttributes(baseDesign);
    // ✅ Use safeArray so JSON strings from DB don't break the file manager
    setKeptPreviewImages(safeArray(baseDesign.previewImages));
    setKeptEditableFiles(safeArray(baseDesign.editableFileUrls));
    setSelectedPolicyId(baseDesign.policyId || '');
    setSelectedFaqIds([]);
    setOpenDesignDialog(true);
    try {
      const res = await Axios.fetch(`design/admin/design-items/${designId}`);
      const full = res?.data?.data;
      if (full) {
        setSelectedDesign(full);
        setKeptEditableFiles(safeArray(full.editableFileUrls));
        setSelectedPolicyId(full.policyId || '');
        setSelectedFaqIds((full.faqs || []).map(f => f.id));
      }
    } catch { /* Non-fatal */ }
  };

  const handleViewDesign = async (design) => {
    setViewingDesign(design);
    setOpenViewDialog(true);
    const [p, f, fullRes] = await Promise.all([
      fetchPricings(design.id),
      fetchFeatures(design.id),
      Axios.fetch(`design/admin/design-items/${design.id}`).catch(() => null),
    ]);
    setViewPricings(p);
    setViewFeatures(f);
    if (fullRes?.data?.data) setViewingDesign(prev => ({ ...prev, ...fullRes.data.data }));
  };

  const handleDeleteDesign = async (designId) => {
    if (!window.confirm('Delete this design?')) return;
    Loading.circle('Deleting...');
    try {
      await Axios.delete(`design/design-items/${designId}`);
      Notify.success('Design deleted!');
      fetchCategoryData();
    } catch (error) { Notify.failure(error.response?.data?.message || 'Failed to delete design'); }
    finally { Loading.remove(); }
  };

  const closeDesignDialog = () => {
    setOpenDesignDialog(false); resetDesign(); setSelectedDesign(null);
    setSelectedAttributes([]); setKeptPreviewImages([]); setKeptEditableFiles([]);
    setSelectedPolicyId(''); setSelectedFaqIds([]);
  };

  // ===== PRICING OPERATIONS =====
  const handleOpenPricing = async (design) => { setPricingDesign(design); const p = await fetchPricings(design.id); setPricings(p); setOpenPricingDialog(true); };
  const handlePricingSubmit = async (data) => {
    Loading.circle('Processing...');
    try {
      const payload = { countryId: data.countryId, licensingType: data.licensingType, originalPrice: parseFloat(data.originalPrice), discountedPrice: data.discountedPrice ? parseFloat(data.discountedPrice) : null, discountPercent: data.discountPercent ? parseInt(data.discountPercent) : null };
      if (selectedPricing) { await Axios.put(`design/design-items/${pricingDesign.id}/pricings/${selectedPricing.id}`, payload); Notify.success('Pricing updated!'); }
      else { await Axios.post(`design/design-items/${pricingDesign.id}/pricings`, payload); Notify.success('Pricing added!'); }
      resetPricing(); setSelectedPricing(null);
      const p = await fetchPricings(pricingDesign.id); setPricings(p);
    } catch (error) { Notify.failure(error.response?.data?.message || 'Failed to save pricing'); }
    finally { Loading.remove(); }
  };
  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm('Delete this pricing?')) return;
    try { await Axios.delete(`design/design-items/${pricingDesign.id}/pricings/${pricingId}`); Notify.success('Pricing deleted!'); const p = await fetchPricings(pricingDesign.id); setPricings(p); }
    catch (error) { Notify.failure(error.response?.data?.message || 'Failed to delete pricing'); }
  };
  const handleEditPricing = (pricing) => { setSelectedPricing(pricing); setValuePricing('countryId', pricing.countryId); setValuePricing('licensingType', pricing.licensingType); setValuePricing('originalPrice', pricing.originalPrice); setValuePricing('discountedPrice', pricing.discountedPrice || ''); setValuePricing('discountPercent', pricing.discountPercent || ''); };
  const closePricingDialog = () => { setOpenPricingDialog(false); resetPricing(); setSelectedPricing(null); setPricingDesign(null); setPricings([]); };

  // ===== FEATURE OPERATIONS =====
  const fetchFeatures = async (designId) => {
    try { const response = await Axios.fetch(`design/design-items/${designId}/features`); return response.data.data || []; }
    catch { Notify.failure('Failed to load features'); return []; }
  };
  const handleOpenFeatures = async (design, fromView = false) => {
    setFeaturesOpenedFromView(fromView); setFeaturesDesign(design);
    const f = await fetchFeatures(design.id); setFeatures(f);
    setEditingFeature(null); resetFeature(); setFeatureThumbnailFile(null); setOpenFeaturesDialog(true);
  };
  const handleFeatureSubmit = async (data) => {
    Loading.circle('Saving feature...');
    try {
      const payload = { title: data.featureTitle, description: data.featureDescription || '', order: parseInt(data.featureOrder) || 0 };
      if (editingFeature) {
        if (featureThumbnailFile) { const fd = new FormData(); fd.append('title', payload.title); fd.append('description', payload.description); fd.append('order', payload.order); fd.append('thumbnail', featureThumbnailFile); await Axios.put(`design/design-items/${featuresDesign.id}/features/${editingFeature.id}`, fd, { 'Content-Type': 'multipart/form-data' }); }
        else { await Axios.put(`design/design-items/${featuresDesign.id}/features/${editingFeature.id}`, payload); }
        Notify.success('Feature updated!');
      } else {
        if (featureThumbnailFile) { const fd = new FormData(); fd.append('title', payload.title); fd.append('description', payload.description); fd.append('order', payload.order); fd.append('thumbnail', featureThumbnailFile); await Axios.post(`design/design-items/${featuresDesign.id}/features`, fd, { 'Content-Type': 'multipart/form-data' }); }
        else { await Axios.post(`design/design-items/${featuresDesign.id}/features`, [payload]); }
        Notify.success('Feature added!');
      }
      resetFeature(); setEditingFeature(null); setFeatureThumbnailFile(null);
      const f = await fetchFeatures(featuresDesign.id); setFeatures(f);
    } catch (error) { Notify.failure(error.response?.data?.message || 'Failed to save feature'); }
    finally { Loading.remove(); }
  };
  const handleEditFeature = (feature) => { setEditingFeature(feature); setValueFeature('featureTitle', feature.title || ''); setValueFeature('featureDescription', feature.description || ''); setValueFeature('featureOrder', feature.order ?? 0); setFeatureThumbnailFile(null); };
  const handleDeleteFeature = async (featureId) => {
    if (!window.confirm('Delete this feature?')) return;
    try { await Axios.delete(`design/design-items/${featuresDesign.id}/features/${featureId}`); Notify.success('Feature deleted!'); const f = await fetchFeatures(featuresDesign.id); setFeatures(f); }
    catch (error) { Notify.failure(error.response?.data?.message || 'Failed to delete feature'); }
  };
  const closeFeaturesDialog = () => { setOpenFeaturesDialog(false); resetFeature(); setEditingFeature(null); setFeaturesDesign(null); setFeatures([]); setFeatureThumbnailFile(null); setFeaturesOpenedFromView(false); };
  const handleBackToView = async () => {
    const design = featuresDesign;
    setOpenFeaturesDialog(false); resetFeature(); setEditingFeature(null); setFeaturesDesign(null); setFeatures([]); setFeatureThumbnailFile(null); setFeaturesOpenedFromView(false);
    await handleViewDesign(design);
  };

  // ===== POLICY OPERATIONS =====
  const handleOpenPolicies = async () => { await fetchPolicies(); setOpenPolicyDialog(true); };
  const handlePolicySubmit = async (data) => {
    Loading.circle('Saving...');
    try {
      const payload = { title: data.policyTitle, content: data.policyContent, categoryId, order: parseInt(data.policyOrder) || 0 };
      if (editingPolicy) { await Axios.put(`design/policies/${editingPolicy.id}`, payload); Notify.success('Policy updated!'); }
      else { await Axios.post('design/policies', payload); Notify.success('Policy created!'); }
      resetPolicy(); setEditingPolicy(null); await fetchPolicies();
    } catch (err) { Notify.failure(err.response?.data?.message || 'Failed to save policy'); }
    finally { Loading.remove(); }
  };
  const handleEditPolicy = (policy) => { setEditingPolicy(policy); setValuePolicy('policyTitle', policy.title); setValuePolicy('policyContent', policy.content); setValuePolicy('policyOrder', policy.order ?? 0); };
  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;
    try { await Axios.delete(`design/policies/${policyId}`); Notify.success('Policy deleted!'); await fetchPolicies(); }
    catch { Notify.failure('Failed to delete'); }
  };
  const closePolicyDialog = () => { setOpenPolicyDialog(false); resetPolicy(); setEditingPolicy(null); };

  // ===== FAQ OPERATIONS =====
  const handleOpenFaqs = async () => { await fetchFaqs(); setOpenFaqDialog(true); };
  const handleFaqSubmit = async (data) => {
    Loading.circle('Saving...');
    try {
      const payload = { question: data.faqQuestion, answer: data.faqAnswer, categoryId, order: parseInt(data.faqOrder) || 0 };
      if (editingFaq) { await Axios.put(`design/faqs/${editingFaq.id}`, payload); Notify.success('FAQ updated!'); }
      else { await Axios.post('design/faqs', payload); Notify.success('FAQ created!'); }
      resetFaq(); setEditingFaq(null); await fetchFaqs();
    } catch (err) { Notify.failure(err.response?.data?.message || 'Failed to save FAQ'); }
    finally { Loading.remove(); }
  };
  const handleEditFaq = (faq) => { setEditingFaq(faq); setValueFaq('faqQuestion', faq.question); setValueFaq('faqAnswer', faq.answer); setValueFaq('faqOrder', faq.order ?? 0); };
  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try { await Axios.delete(`design/faqs/${faqId}`); Notify.success('FAQ deleted!'); await fetchFaqs(); }
    catch { Notify.failure('Failed to delete'); }
  };
  const closeFaqDialog = () => { setOpenFaqDialog(false); resetFaq(); setEditingFaq(null); };
  const toggleFaqSelection = (faqId) => { setSelectedFaqIds(prev => prev.includes(faqId) ? prev.filter(id => id !== faqId) : [...prev, faqId]); };

  // ===== TABLE =====
  const designsColumns = [
    { Header: '#', accessor: 'sr', width: '4%' },
    { Header: 'Thumbnail', accessor: 'thumb', width: '8%' },
    { Header: 'Title / Slug', accessor: 'title', width: '20%' },
    { Header: 'Base Price', accessor: 'price', width: '8%' },
    { Header: 'Attributes', accessor: 'attrs', width: '28%' },
    { Header: 'Status', accessor: 'status', width: '8%' },
    { Header: 'Created', accessor: 'createdOn', width: '10%' },
    { Header: 'Actions', accessor: 'action', width: '14%' },
  ];

  const getDesignRow = (design, idx) => ({
    sr: <MDTypography variant="caption" fontWeight="medium">{idx + 1}</MDTypography>,
    thumb: design.thumbnail ? (
      <img src={design.thumbnail} alt={design.title} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
    ) : (
      <Box sx={{ width: 52, height: 52, borderRadius: '6px', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc' }}>
        <ImageIcon sx={{ color: 'grey.400', fontSize: 20 }} />
      </Box>
    ),
    title: (
      <Box>
        <MDTypography variant="button" fontWeight="medium" display="block">{design.title}</MDTypography>
        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>{design.slug}</MDTypography>
      </Box>
    ),
    price: <MDTypography variant="button" color="success" fontWeight="bold">${parseFloat(design.basePrice || 0).toFixed(2)}</MDTypography>,
    attrs: (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {design.attributes?.slice(0, 4).map((attr, i) => (
          <Chip key={i} label={`${attr.attributeType?.name}: ${attr.value}`} size="small" color="info" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
        ))}
        {design.attributes?.length > 4 && <Chip label={`+${design.attributes.length - 4}`} size="small" sx={{ fontSize: 10, height: 20 }} />}
        {(!design.attributes || design.attributes.length === 0) && <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>—</MDTypography>}
      </Box>
    ),
    status: <MDBadge badgeContent={design.isActive ? 'Active' : 'Inactive'} color={design.isActive ? 'success' : 'secondary'} size="sm" />,
    createdOn: <MDTypography variant="caption" color="text">{moment(design.createdAt).fromNow()}</MDTypography>,
    action: (
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {canRead && (<Tooltip title="View details"><IconButton size="small" onClick={() => handleViewDesign(design)} sx={{ color: 'success.main' }}><Visibility fontSize="small" /></IconButton></Tooltip>)}
        {canUpdate && (<Tooltip title="Edit design"><IconButton size="small" onClick={() => handleEditDesign(design.id)} sx={{ color: 'info.main' }}><Edit fontSize="small" /></IconButton></Tooltip>)}
        {canUpdate && (<Tooltip title="Manage pricing"><IconButton size="small" onClick={() => handleOpenPricing(design)} sx={{ color: 'warning.main' }}><AttachMoney fontSize="small" /></IconButton></Tooltip>)}
        {canUpdate && (<Tooltip title="Manage features"><IconButton size="small" onClick={() => handleOpenFeatures(design)} sx={{ color: 'secondary.main' }}><FormatListBulleted fontSize="small" /></IconButton></Tooltip>)}
        {canDelete && (<Tooltip title="Delete design"><IconButton size="small" onClick={() => handleDeleteDesign(design.id)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>)}
      </Box>
    ),
  });

  useEffect(() => {
    if (categoryId && canRead) { fetchCategoryData(); fetchAttributeTypes(); fetchCountries(); fetchPolicies(); fetchFaqs(); }
  }, [categoryId, canRead]);

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>

          {category && (
            <Grid item xs={12}>
              <Card>
                <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
                  {category.heroImage ? (
                    <img src={category.heroImage} alt={category.name} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <Box sx={{ width: '100%', height: 140, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed', borderColor: 'grey.300' }}>
                      <MDTypography variant="caption" color="text">No hero image uploaded</MDTypography>
                    </Box>
                  )}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)', display: 'flex', alignItems: 'flex-end', p: 2 }}>
                    <Box>
                      <MDTypography variant="h5" color="white" fontWeight="bold">{category.name}</MDTypography>
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                        slug: {category.slug} &nbsp;•&nbsp; {designs.length} design{designs.length !== 1 ? 's' : ''} &nbsp;•&nbsp;
                        <MDBadge badgeContent={category.isActive ? 'Active' : 'Inactive'} color={category.isActive ? 'success' : 'secondary'} size="xs" />
                      </MDTypography>
                      {category.description && <MDTypography variant="caption" color="white" display="block" sx={{ opacity: 0.75, mt: 0.5 }}>{category.description}</MDTypography>}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <MDTypography variant="h6" color="white">Designs &nbsp;<span style={{ fontSize: 13, opacity: 0.8 }}>({designs.length})</span></MDTypography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {canCreate && (<MDButton size="small" variant="gradient" color="white" startIcon={<Add />} onClick={() => { setSelectedDesign(null); setSelectedAttributes([]); resetDesign(); setSelectedPolicyId(''); setSelectedFaqIds([]); setOpenDesignDialog(true); }}>Add Design</MDButton>)}
                  <MDButton size="small" variant="outlined" color="white" startIcon={<PolicyIcon />} onClick={handleOpenPolicies}>Policies ({policies.length})</MDButton>
                  <MDButton size="small" variant="outlined" color="white" startIcon={<QuestionAnswer />} onClick={handleOpenFaqs}>FAQs ({faqs.length})</MDButton>
                  <MDButton size="small" variant="outlined" color="white" startIcon={<ArrowBack />} onClick={() => navigate('/admin/designs')}>Back</MDButton>
                </Box>
              </MDBox>
              <MDBox pt={3}>
                <DataTable table={{ columns: designsColumns, rows: designs.map((design, idx) => getDesignRow(design, idx)) }} isSorted={false} entriesPerPage={false} hoverEffect />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ===== VIEW DESIGN DIALOG ===== */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{viewingDesign?.title}</Typography>
            <IconButton size="small" onClick={() => setOpenViewDialog(false)}><Close /></IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">{viewingDesign?.slug}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {viewingDesign && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>Images</MDTypography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {viewingDesign.thumbnail && (
                    <Box sx={{ position: 'relative' }}>
                      <img src={viewingDesign.thumbnail} alt="Thumbnail" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '2px solid #1976d2' }} />
                      <Chip label="Thumbnail" size="small" color="info" sx={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10, height: 18 }} />
                    </Box>
                  )}
                  {/* ✅ safeArray prevents crash when previewImages is a JSON string */}
                  {safeArray(viewingDesign.previewImages).map((url, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <img src={url} alt={`Preview ${i + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                      <Chip label={`Preview ${i + 1}`} size="small" sx={{ position: 'absolute', bottom: 4, left: 4, fontSize: 10, height: 18, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }} />
                    </Box>
                  ))}
                  {!viewingDesign.thumbnail && safeArray(viewingDesign.previewImages).length === 0 && (
                    <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, color: 'grey.400' }}>
                      <MDTypography variant="caption">No images uploaded</MDTypography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={2} sx={{ mb: 1.5 }}>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">Base Price</MDTypography>
                  <MDTypography variant="button" color="success" fontWeight="bold">${parseFloat(viewingDesign.basePrice || 0).toFixed(2)}</MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">Licensing</MDTypography>
                  <Chip label={viewingDesign.licensingType} size="small" color="info" variant="outlined" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">Status</MDTypography>
                  <MDBadge badgeContent={viewingDesign.isActive ? 'Active' : 'Inactive'} color={viewingDesign.isActive ? 'success' : 'secondary'} size="sm" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text" display="block">Created</MDTypography>
                  <MDTypography variant="caption">{moment(viewingDesign.createdAt).format('DD MMM YYYY')}</MDTypography>
                </Grid>
              </Grid>

              {viewingDesign.shortDescription && (
                <Box sx={{ mb: 1.5 }}>
                  <MDTypography variant="caption" color="text" display="block">Short Description</MDTypography>
                  <MDTypography variant="body2">{viewingDesign.shortDescription}</MDTypography>
                </Box>
              )}

              {viewingDesign.attributes?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>Attributes</MDTypography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {viewingDesign.attributes.map((attr, i) => (
                      <Chip key={i} size="small" color="info" variant="outlined" label={`${attr.attributeType?.name || attr.attributeTypeId}: ${attr.value}`} />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <MDTypography variant="button" fontWeight="medium">Features ({viewFeatures.length})</MDTypography>
                  <Tooltip title="Add / edit features">
                    <Chip icon={<FormatListBulleted fontSize="small" />} label="Manage" size="small" color="secondary" variant="outlined" onClick={() => { setOpenViewDialog(false); handleOpenFeatures(viewingDesign, true); }} sx={{ cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                {viewFeatures.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 1.5 }}>
                    {viewFeatures.map((f) => (
                      <Box key={f.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, textAlign: 'center' }}>
                        <Box sx={{ width: '100%', aspectRatio: '1', borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {f.thumbnail ? <img src={f.thumbnail} alt={f.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <FormatListBulleted sx={{ fontSize: 22, color: 'grey.400' }} />}
                        </Box>
                        <MDTypography variant="caption" fontWeight="medium" sx={{ fontSize: 11, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{f.title}</MDTypography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormatListBulleted sx={{ color: 'grey.400', fontSize: 18 }} />
                    <MDTypography variant="caption" color="text">No features added yet.{' '}<span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setOpenViewDialog(false); handleOpenFeatures(viewingDesign, true); }}>Add features</span></MDTypography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ mb: 1.5 }}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Editable Files ({safeArray(viewingDesign.editableFileUrls).length})
                  <MDTypography variant="caption" color="text" sx={{ ml: 1, fontSize: 10 }}>Private — click to download</MDTypography>
                </MDTypography>
                {/* ✅ safeArray prevents crash when editableFileUrls is a JSON string */}
                {safeArray(viewingDesign.editableFileUrls).length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {safeArray(viewingDesign.editableFileUrls).map((f, i) => {
                      const ext = getFileExt(f.fileName);
                      const color = getFileColor(ext);
                      return (
                        <Tooltip key={i} title={`Download ${f.fileName || `File ${i + 1}`}`}>
                          <Box onClick={() => handleDownloadEditableFile(viewingDesign.id, i, f.fileName)} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 2, px: 1.5, py: 1, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: 'grey.50', borderColor: color, boxShadow: 1 }, maxWidth: 220 }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: color + '18', flexShrink: 0 }}><InsertDriveFile sx={{ color, fontSize: 20 }} /></Box>
                            <Box sx={{ overflow: 'hidden', flex: 1 }}>
                              <MDTypography variant="caption" fontWeight="medium" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName || `File ${i + 1}`}</MDTypography>
                              <MDTypography variant="caption" color="text" sx={{ fontSize: 10, textTransform: 'uppercase', color }}>.{ext || '—'}</MDTypography>
                            </Box>
                            <Download sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: 2 }}><MDTypography variant="caption" color="text">No editable files uploaded.</MDTypography></Box>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ mb: 1.5 }}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>Use Policy</MDTypography>
                {viewingDesign.policy ? (
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>{viewingDesign.policy.title}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ whiteSpace: 'pre-line', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', opacity: 0.7 }}>{viewingDesign.policy.content}</MDTypography>
                  </Box>
                ) : <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>No policy assigned</MDTypography>}
              </Box>

              {viewingDesign.faqs?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>FAQs ({viewingDesign.faqs.length})</MDTypography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {viewingDesign.faqs.map((faq, i) => (
                      <Box key={faq.id} sx={{ p: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}>
                        <MDTypography variant="caption" fontWeight="bold" display="block">Q{i + 1}. {faq.question}</MDTypography>
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.65, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{faq.answer}</MDTypography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ✅ safeArray prevents crash when formats is a JSON string */}
              {safeArray(viewingDesign.formats).length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>File Formats</MDTypography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {safeArray(viewingDesign.formats).map(fmt => (
                      <Chip key={fmt} label={fmt} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5 }} />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>Pricings ({viewPricings.length})</MDTypography>
              {viewPricings.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.25 }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.25 }}>License</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.25 }}>Original</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.25 }}>Discounted</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.25 }}>Discount %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewPricings.map((p) => (
                        <TableRow key={p.id} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontSize: 13, py: 1.25 }}>
                            {p.country?.flagEmoji} {p.country?.countryName}
                            <Typography variant="caption" display="block" color="text.secondary">{p.country?.currencyCode} {p.country?.currencySymbol}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25 }}><Chip label={p.licensingType} size="small" color="info" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 600, py: 1.25 }}>{p.country?.currencySymbol}{parseFloat(p.originalPrice).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontSize: 13, py: 1.25 }}>{p.discountedPrice ? <span style={{ color: '#2e7d32', fontWeight: 600 }}>{p.country?.currencySymbol}{parseFloat(p.discountedPrice).toFixed(2)}</span> : <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                          <TableCell sx={{ py: 1.25 }}>{p.discountPercent ? <Chip label={`${p.discountPercent}% off`} size="small" color="success" /> : <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}><MDTypography variant="caption" color="text">No pricings set for this design.</MDTypography></Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => { setOpenViewDialog(false); handleEditDesign(viewingDesign?.id); }} color="info">Edit Design</MDButton>
          <MDButton onClick={() => { setOpenViewDialog(false); handleOpenPricing(viewingDesign); }} color="warning">Manage Pricing</MDButton>
          <MDButton onClick={() => { setOpenViewDialog(false); handleOpenFeatures(viewingDesign, true); }} color="secondary" startIcon={<FormatListBulleted />}>Features</MDButton>
          <MDButton onClick={() => setOpenViewDialog(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* ===== CREATE / EDIT DESIGN DIALOG ===== */}
      <Dialog open={openDesignDialog} onClose={closeDesignDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedDesign ? `Edit — ${selectedDesign.title}` : 'Add New Design'}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitDesign(handleDesignSubmit)}>
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12} md={6}><TextField label="Title *" fullWidth {...registerDesign('title', { required: 'Title required' })} error={!!designErrors.title} helperText={designErrors.title?.message} /></Grid>
              <Grid item xs={12} md={6}><TextField label="Slug *" fullWidth {...registerDesign('slug', { required: 'Slug required' })} error={!!designErrors.slug} helperText={designErrors.slug?.message} /></Grid>
              <Grid item xs={12} md={6}><TextField label="Base Price *" type="number" fullWidth inputProps={{ step: '0.01', min: '0' }} {...registerDesign('basePrice', { required: 'Price required' })} error={!!designErrors.basePrice} helperText={designErrors.basePrice?.message} /></Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Licensing Type</InputLabel>
                  <Select label="Licensing Type" defaultValue="standard" {...registerDesign('licensingType')}>
                    {LICENSING_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField label="Short Description" multiline rows={2} fullWidth {...registerDesign('shortDescription')} /></Grid>
              <Grid item xs={12}><TextField label="Long Description" multiline rows={4} fullWidth {...registerDesign('longDescription')} /></Grid>
            </Grid>

            {attributeTypes.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Divider sx={{ mb: 2 }} />
                <MDTypography variant="button" fontWeight="medium">Attributes</MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={1.5}>Tag this design so users can filter and find it.</MDTypography>
                <Grid container spacing={2}>
                  {attributeTypes.map((attrType) => (
                    <Grid item xs={12} md={6} key={attrType.id}>
                      {(attrType.type === 'enum' || attrType.type === 'multi_enum') && attrType.options?.length > 0 ? (
                        <Box>
                          <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.75}>{attrType.name}{attrType.type === 'multi_enum' && <Chip label="multi" size="small" sx={{ ml: 0.5, fontSize: 9, height: 16 }} />}</MDTypography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {attrType.options.map((opt) => (
                              <Chip key={opt.value} label={opt.label} size="small" color={isAttrValueSelected(attrType.id, opt.value) ? 'info' : 'default'} variant={isAttrValueSelected(attrType.id, opt.value) ? 'filled' : 'outlined'} onClick={() => handleAttributeChange(attrType.id, opt.value, attrType)} sx={{ cursor: 'pointer' }} />
                            ))}
                          </Box>
                        </Box>
                      ) : attrType.type === 'boolean' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, px: 1.5, py: 0.75 }}>
                          <Box>
                            <MDTypography variant="caption" fontWeight="medium" display="block">{attrType.name}</MDTypography>
                            <MDTypography variant="caption" color="text" sx={{ fontSize: 10 }}>{getSelectedValueForAttrType(attrType.id) === 'true' ? 'Yes / True' : getSelectedValueForAttrType(attrType.id) === 'false' ? 'No / False' : 'Not set'}</MDTypography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Chip label="Yes" size="small" color={getSelectedValueForAttrType(attrType.id) === 'true' ? 'success' : 'default'} variant={getSelectedValueForAttrType(attrType.id) === 'true' ? 'filled' : 'outlined'} onClick={() => handleAttributeChange(attrType.id, 'true', attrType)} sx={{ cursor: 'pointer', fontWeight: 600 }} />
                            <Chip label="No" size="small" color={getSelectedValueForAttrType(attrType.id) === 'false' ? 'error' : 'default'} variant={getSelectedValueForAttrType(attrType.id) === 'false' ? 'filled' : 'outlined'} onClick={() => handleAttributeChange(attrType.id, 'false', attrType)} sx={{ cursor: 'pointer', fontWeight: 600 }} />
                          </Box>
                        </Box>
                      ) : (
                        <TextField label={attrType.name} fullWidth size="small" type={attrType.type === 'number' ? 'number' : 'text'} value={getSelectedValueForAttrType(attrType.id)} onChange={(e) => handleAttributeChange(attrType.id, e.target.value, attrType)} helperText={`Type: ${attrType.type}`} inputProps={attrType.type === 'number' ? { step: 'any' } : {}} />
                      )}
                    </Grid>
                  ))}
                </Grid>
                {selectedAttributes.length > 0 && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>Selected:</MDTypography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedAttributes.map((a, i) => {
                        const at = attributeTypes.find(x => x.id === a.attributeTypeId);
                        return <Chip key={i} size="small" color="info" label={`${at?.name || ''}: ${a.value}`} onDelete={() => setSelectedAttributes(prev => prev.filter((_, idx) => idx !== i))} />;
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2 }} />
              <MDTypography variant="button" fontWeight="medium">Files</MDTypography>
              {selectedDesign ? (
                <Box sx={{ mt: 1.5 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                        <MDTypography variant="caption" fontWeight="bold" display="block" mb={1}>Thumbnail</MDTypography>
                        {selectedDesign.thumbnail ? (
                          <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                            <img src={selectedDesign.thumbnail} alt="Thumbnail" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '2px solid #1976d2', display: 'block' }} />
                            <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ fontSize: 10, opacity: 0.7 }}>Current</MDTypography>
                          </Box>
                        ) : (
                          <Box sx={{ width: 100, height: 100, border: '1px dashed #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>None</MDTypography></Box>
                        )}
                        <MDTypography variant="caption" color="text" display="block" mb={0.5} sx={{ fontSize: 10 }}>Upload to replace:</MDTypography>
                        <Input type="file" fullWidth inputProps={{ accept: 'image/*' }} {...registerDesign('thumbnail')} />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <MDTypography variant="caption" fontWeight="bold">Preview Images ({keptPreviewImages.length} kept)</MDTypography>
                          {keptPreviewImages.length > 0 && <Chip label="Remove all" size="small" color="error" variant="outlined" onClick={() => setKeptPreviewImages([])} sx={{ fontSize: 10, cursor: 'pointer' }} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {keptPreviewImages.map((url, i) => (
                            <Box key={url} sx={{ position: 'relative' }}>
                              <img src={url} alt={`Preview ${i + 1}`} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', display: 'block' }} />
                              <Box onClick={() => setKeptPreviewImages(prev => prev.filter(u => u !== url))} sx={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', bgcolor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: 1 }}>
                                <Close sx={{ fontSize: 12, color: '#fff' }} />
                              </Box>
                            </Box>
                          ))}
                          {keptPreviewImages.length === 0 && <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>All preview images removed</MDTypography>}
                        </Box>
                        <MDTypography variant="caption" color="text" display="block" mb={0.5} sx={{ fontSize: 10 }}>Add more images (appended to kept ones):</MDTypography>
                        <Input type="file" fullWidth inputProps={{ multiple: true, accept: 'image/*' }} {...registerDesign('previewImages')} />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <MDTypography variant="caption" fontWeight="bold">Editable Files ({keptEditableFiles.length} kept)</MDTypography>
                          {keptEditableFiles.length > 0 && <Chip label="Remove all" size="small" color="error" variant="outlined" onClick={() => setKeptEditableFiles([])} sx={{ fontSize: 10, cursor: 'pointer' }} />}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                          {keptEditableFiles.map((f, i) => {
                            const ext = getFileExt(f.fileName);
                            const color = getFileColor(ext);
                            return (
                              <Box key={f.key} sx={{ position: 'relative' }}>
                                <Tooltip title="Click to download · Red × to remove">
                                  <Box onClick={() => handleDownloadEditableFile(selectedDesign.id, safeArray(selectedDesign.editableFileUrls).findIndex(ef => ef.key === f.key), f.fileName)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, border: '1px solid', borderColor: 'grey.300', borderRadius: 1.5, px: 1.25, py: 0.75, cursor: 'pointer', pr: 3, '&:hover': { bgcolor: 'grey.50', borderColor: color }, maxWidth: 200 }}>
                                    <InsertDriveFile sx={{ color, fontSize: 18, flexShrink: 0 }} />
                                    <Box sx={{ overflow: 'hidden' }}>
                                      <MDTypography variant="caption" fontWeight="medium" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{f.fileName || `File ${i + 1}`}</MDTypography>
                                      <MDTypography variant="caption" sx={{ fontSize: 9, textTransform: 'uppercase', color }}>.{ext || '—'}</MDTypography>
                                    </Box>
                                    <Download sx={{ fontSize: 13, color: 'grey.400', flexShrink: 0 }} />
                                  </Box>
                                </Tooltip>
                                <Box onClick={() => setKeptEditableFiles(prev => prev.filter(ef => ef.key !== f.key))} sx={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', bgcolor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: 1, zIndex: 1 }}>
                                  <Close sx={{ fontSize: 12, color: '#fff' }} />
                                </Box>
                              </Box>
                            );
                          })}
                          {keptEditableFiles.length === 0 && <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>All editable files removed</MDTypography>}
                        </Box>
                        <MDTypography variant="caption" color="text" display="block" mb={0.5} sx={{ fontSize: 10 }}>Add new files: .fig .svg .ai .psd .pdf .jpg .png .zip etc.</MDTypography>
                        <Input type="file" fullWidth inputProps={{ multiple: true }} {...registerDesign('editableFiles')} />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={4}><MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>Thumbnail</MDTypography><Input type="file" fullWidth inputProps={{ accept: 'image/*' }} {...registerDesign('thumbnail')} /></Grid>
                  <Grid item xs={12} md={4}><MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>Preview Images (multi)</MDTypography><Input type="file" fullWidth inputProps={{ multiple: true, accept: 'image/*' }} {...registerDesign('previewImages')} /></Grid>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>Editable Files — private</MDTypography>
                    <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: 10, mb: 0.5 }}>.fig .svg .ai .psd .pdf .jpg .png .zip etc.</MDTypography>
                    <Input type="file" fullWidth inputProps={{ multiple: true }} {...registerDesign('editableFiles')} />
                  </Grid>
                </Grid>
              )}
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2 }} />
              <MDTypography variant="button" fontWeight="medium">Use Policy &amp; FAQs</MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={2} sx={{ opacity: 0.7 }}>Assign a use policy and FAQ items from this category's pool.</MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Use Policy</InputLabel>
                    <Select label="Use Policy" value={selectedPolicyId} onChange={(e) => setSelectedPolicyId(e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {policies.map(p => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {policies.length === 0 && <MDTypography variant="caption" color="text" sx={{ fontSize: 10, mt: 0.5, display: 'block' }}>No policies yet — <span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { closeDesignDialog(); handleOpenPolicies(); }}>create one first</span></MDTypography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.75}>FAQs (select all that apply)</MDTypography>
                  {faqs.length === 0 ? (
                    <MDTypography variant="caption" color="text" sx={{ fontSize: 10 }}>No FAQs yet — <span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { closeDesignDialog(); handleOpenFaqs(); }}>create some first</span></MDTypography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {faqs.map(faq => (
                        <Chip key={faq.id} label={faq.question.length > 40 ? faq.question.slice(0, 40) + '…' : faq.question} size="small" color={selectedFaqIds.includes(faq.id) ? 'info' : 'default'} variant={selectedFaqIds.includes(faq.id) ? 'filled' : 'outlined'} onClick={() => toggleFaqSelection(faq.id)} sx={{ cursor: 'pointer', maxWidth: 240 }} />
                      ))}
                    </Box>
                  )}
                  {selectedFaqIds.length > 0 && <MDTypography variant="caption" color="text" sx={{ fontSize: 10, mt: 0.75, display: 'block' }}>{selectedFaqIds.length} FAQ{selectedFaqIds.length > 1 ? 's' : ''} selected</MDTypography>}
                </Grid>
              </Grid>
            </Box>

            <DialogActions sx={{ mt: 2.5, px: 0 }}>
              <MDButton onClick={closeDesignDialog}>Cancel</MDButton>
              <MDButton type="submit" color="info" variant="contained">{selectedDesign ? 'Update Design' : 'Create Design'}</MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== PRICING DIALOG ===== */}
      <Dialog open={openPricingDialog} onClose={closePricingDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><Typography variant="h6">Pricing</Typography><Typography variant="caption" color="text.secondary">{pricingDesign?.title}</Typography></Box>
            <IconButton size="small" onClick={closePricingDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Current Pricings ({pricings.length})</MDTypography>
              {pricings.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 130 }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 90 }}>License</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 90 }}>Original</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 90 }}>Final</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 70 }}>Disc%</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 1.5, minWidth: 80 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pricings.map((p) => (
                        <TableRow key={p.id} sx={{ bgcolor: selectedPricing?.id === p.id ? 'info.50' : 'inherit', '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'grey.50' } }}>
                          <TableCell sx={{ py: 1.5 }}><Typography variant="body2" fontWeight={500}>{p.country?.flagEmoji} {p.country?.countryName}</Typography><Typography variant="caption" color="text.secondary">{p.country?.currencyCode}</Typography></TableCell>
                          <TableCell sx={{ py: 1.5 }}><Chip label={p.licensingType} size="small" color="info" variant="outlined" /></TableCell>
                          <TableCell sx={{ py: 1.5 }}><Typography variant="body2" fontWeight={500}>{p.country?.currencySymbol}{parseFloat(p.originalPrice).toFixed(2)}</Typography></TableCell>
                          <TableCell sx={{ py: 1.5 }}>{p.discountedPrice ? <Typography variant="body2" fontWeight={600} color="success.main">{p.country?.currencySymbol}{parseFloat(p.discountedPrice).toFixed(2)}</Typography> : <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>{p.discountPercent ? <Chip label={`${p.discountPercent}%`} size="small" color="success" /> : <Typography variant="body2" color="text.secondary">—</Typography>}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            {canUpdate && (<Tooltip title="Edit"><IconButton size="small" color="info" onClick={() => handleEditPricing(p)}><Edit fontSize="small" /></IconButton></Tooltip>)}
                            {canDelete && (<Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeletePricing(p.id)}><Delete fontSize="small" /></IconButton></Tooltip>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Box sx={{ p: 3, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}><MDTypography variant="caption" color="text">No pricings yet. Add one on the right.</MDTypography></Box>}
            </Grid>
            {canUpdate && (
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={2}>{selectedPricing ? '✏️ Edit Pricing' : '+ Add Pricing'}</MDTypography>
                  <form onSubmit={handleSubmitPricing(handlePricingSubmit)}>
                    <FormControl fullWidth sx={{ mb: 2 }} error={!!pricingErrors.countryId}>
                      <InputLabel>Country *</InputLabel>
                      <Select label="Country *" defaultValue="" {...registerPricing('countryId', { required: 'Required' })}>
                        {countries.length === 0 && <MenuItem disabled>Loading...</MenuItem>}
                        {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.flagEmoji} {c.countryName} ({c.currencyCode} {c.currencySymbol})</MenuItem>)}
                      </Select>
                      {pricingErrors.countryId && <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>{pricingErrors.countryId.message}</Typography>}
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}><InputLabel>Licensing Type *</InputLabel><Select label="Licensing Type *" defaultValue="standard" {...registerPricing('licensingType', { required: true })}>{LICENSING_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</Select></FormControl>
                    <TextField label="Original Price *" type="number" fullWidth sx={{ mb: 2 }} inputProps={{ step: '0.01', min: '0' }} {...registerPricing('originalPrice', { required: 'Required' })} error={!!pricingErrors.originalPrice} helperText={pricingErrors.originalPrice?.message} />
                    <TextField label="Discounted Price" type="number" fullWidth sx={{ mb: 2 }} inputProps={{ step: '0.01', min: '0' }} {...registerPricing('discountedPrice')} helperText="Leave blank if no discount" />
                    <TextField label="Discount %" type="number" fullWidth sx={{ mb: 2.5 }} inputProps={{ min: '0', max: '100' }} {...registerPricing('discountPercent')} helperText="0–100" />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedPricing && <MDButton fullWidth color="secondary" size="small" onClick={() => { setSelectedPricing(null); resetPricing(); }}>Cancel Edit</MDButton>}
                      <MDButton fullWidth type="submit" color="info" variant="contained">{selectedPricing ? 'Update' : 'Add Pricing'}</MDButton>
                    </Box>
                  </form>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><MDButton onClick={closePricingDialog} variant="outlined">Done</MDButton></DialogActions>
      </Dialog>

      {/* ===== FEATURES DIALOG ===== */}
      <Dialog open={openFeaturesDialog} onClose={closeFeaturesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><Typography variant="h6">Features</Typography><Typography variant="caption" color="text.secondary">{featuresDesign?.title}</Typography></Box>
            <IconButton size="small" onClick={closeFeaturesDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Current Features ({features.length})</MDTypography>
              {features.length === 0 ? (
                <Box sx={{ p: 3, border: '2px dashed', borderColor: 'grey.200', borderRadius: 2, textAlign: 'center' }}><FormatListBulleted sx={{ fontSize: 36, color: 'grey.300', display: 'block', mx: 'auto', mb: 1 }} /><MDTypography variant="caption" color="text">No features yet.</MDTypography></Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {features.map((f) => (
                    <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid', borderColor: editingFeature?.id === f.id ? 'info.main' : 'grey.200', borderRadius: 2, bgcolor: editingFeature?.id === f.id ? 'info.50' : 'transparent', transition: 'all 0.15s' }}>
                      {f.thumbnail ? <img src={f.thumbnail} alt={f.title} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', flexShrink: 0, display: 'block' }} /> : <FeatureThumbnailPlaceholder size={48} />}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MDTypography variant="button" fontWeight="medium" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.title}</MDTypography>
                        {f.description && <MDTypography variant="caption" color="text" sx={{ opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{f.description}</MDTypography>}
                        <MDTypography variant="caption" color="text" sx={{ fontSize: 10, opacity: 0.5 }}>Order: {f.order} {f.thumbnail ? '· has thumbnail' : '· no thumbnail'}</MDTypography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                        {canUpdate && (<Tooltip title="Edit"><IconButton size="small" color="info" onClick={() => handleEditFeature(f)}><Edit fontSize="small" /></IconButton></Tooltip>)}
                        {canDelete && (<Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteFeature(f.id)}><Delete fontSize="small" /></IconButton></Tooltip>)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
            {canUpdate && (
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={2}>{editingFeature ? '✏️ Edit Feature' : '+ Add Feature'}</MDTypography>
                  <form onSubmit={handleSubmitFeature(handleFeatureSubmit)}>
                    <TextField label="Title *" fullWidth sx={{ mb: 2 }} placeholder="e.g. Instant Download" {...registerFeature('featureTitle', { required: true })} />
                    <TextField label="Description" fullWidth multiline rows={2} sx={{ mb: 2 }} placeholder="Short description of this feature" {...registerFeature('featureDescription')} />
                    <Box sx={{ mb: 2 }}>
                      <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>Thumbnail</MDTypography>
                      <MDTypography variant="caption" color="text" display="block" mb={1} sx={{ fontSize: 10, lineHeight: 1.4 }}>Small image shown above the feature title. Recommended: square, 200×200px.</MDTypography>
                      {editingFeature?.thumbnail && !featureThumbnailFile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                          <img src={editingFeature.thumbnail} alt="Current thumbnail" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '2px solid #1976d2', display: 'block', flexShrink: 0 }} />
                          <Box><MDTypography variant="caption" fontWeight="medium" display="block">Current thumbnail</MDTypography><MDTypography variant="caption" color="text" sx={{ fontSize: 10, opacity: 0.65 }}>Upload a new image below to replace it.</MDTypography></Box>
                        </Box>
                      )}
                      {featureThumbnailFile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1, border: '1px solid', borderColor: 'success.light', borderRadius: 2, bgcolor: 'success.50' }}>
                          <img src={URL.createObjectURL(featureThumbnailFile)} alt="New thumbnail preview" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '2px solid #2e7d32', display: 'block', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}><MDTypography variant="caption" fontWeight="medium" display="block">New thumbnail selected</MDTypography><MDTypography variant="caption" color="text" sx={{ fontSize: 10, opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{featureThumbnailFile.name}</MDTypography></Box>
                          <IconButton size="small" onClick={() => setFeatureThumbnailFile(null)}><Close sx={{ fontSize: 14 }} /></IconButton>
                        </Box>
                      )}
                      <input type="file" accept="image/*" style={{ fontSize: 12 }} onChange={(e) => setFeatureThumbnailFile(e.target.files?.[0] || null)} />
                    </Box>
                    <TextField label="Order" type="number" fullWidth size="small" sx={{ mb: 2.5 }} defaultValue={0} inputProps={{ min: 0 }} helperText="Lower = appears first" {...registerFeature('featureOrder')} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {editingFeature && <MDButton fullWidth size="small" color="secondary" onClick={() => { setEditingFeature(null); resetFeature(); setFeatureThumbnailFile(null); }}>Cancel</MDButton>}
                      <MDButton fullWidth type="submit" color="info" variant="contained">{editingFeature ? 'Update' : 'Add Feature'}</MDButton>
                    </Box>
                  </form>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between' }}>
          {featuresOpenedFromView ? <MDButton onClick={handleBackToView} variant="outlined" color="info" startIcon={<ArrowBack />}>Back to Design</MDButton> : <Box />}
          <MDButton onClick={closeFeaturesDialog} variant="outlined">Done</MDButton>
        </DialogActions>
      </Dialog>

      {/* ===== POLICY DIALOG ===== */}
      <Dialog open={openPolicyDialog} onClose={closePolicyDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><Typography variant="h6">Use Policies</Typography><Typography variant="caption" color="text.secondary">Category: {category?.name}</Typography></Box>
            <IconButton size="small" onClick={closePolicyDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Current Policies ({policies.length})</MDTypography>
              {policies.length === 0 ? <Box sx={{ p: 3, border: '2px dashed', borderColor: 'grey.200', borderRadius: 2, textAlign: 'center' }}><PolicyIcon sx={{ fontSize: 36, color: 'grey.300', display: 'block', mx: 'auto', mb: 1 }} /><MDTypography variant="caption" color="text">No policies yet.</MDTypography></Box> : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {policies.map(p => (
                    <Box key={p.id} sx={{ p: 2, border: '1px solid', borderRadius: 2, borderColor: editingPolicy?.id === p.id ? 'info.main' : 'grey.200', bgcolor: editingPolicy?.id === p.id ? 'info.50' : 'transparent' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, mr: 1 }}><MDTypography variant="button" fontWeight="bold" display="block">{p.title}</MDTypography><MDTypography variant="caption" color="text" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line', opacity: 0.7, mt: 0.5 }}>{p.content}</MDTypography></Box>
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                          {canUpdate && (<Tooltip title="Edit"><IconButton size="small" color="info" onClick={() => handleEditPolicy(p)}><Edit fontSize="small" /></IconButton></Tooltip>)}
                          {canDelete && (<Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeletePolicy(p.id)}><Delete fontSize="small" /></IconButton></Tooltip>)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
            {(editingPolicy ? canUpdate : canCreate) && (
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={2}>{editingPolicy ? '✏️ Edit Policy' : '+ Create Policy'}</MDTypography>
                  <form onSubmit={handleSubmitPolicy(handlePolicySubmit)}>
                    <TextField label="Title *" fullWidth sx={{ mb: 2 }} {...registerPolicy('policyTitle', { required: true })} />
                    <TextField label="Content *" fullWidth multiline rows={8} sx={{ mb: 2 }} {...registerPolicy('policyContent', { required: true })} />
                    <TextField label="Order" type="number" fullWidth size="small" sx={{ mb: 2.5 }} defaultValue={0} inputProps={{ min: 0 }} {...registerPolicy('policyOrder')} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {editingPolicy && <MDButton fullWidth size="small" color="secondary" onClick={() => { setEditingPolicy(null); resetPolicy(); }}>Cancel</MDButton>}
                      <MDButton fullWidth type="submit" color="info" variant="contained">{editingPolicy ? 'Update' : 'Create Policy'}</MDButton>
                    </Box>
                  </form>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><MDButton onClick={closePolicyDialog} variant="outlined">Done</MDButton></DialogActions>
      </Dialog>

      {/* ===== FAQ DIALOG ===== */}
      <Dialog open={openFaqDialog} onClose={closeFaqDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><Typography variant="h6">FAQs</Typography><Typography variant="caption" color="text.secondary">Category: {category?.name}</Typography></Box>
            <IconButton size="small" onClick={closeFaqDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Current FAQs ({faqs.length})</MDTypography>
              {faqs.length === 0 ? <Box sx={{ p: 3, border: '2px dashed', borderColor: 'grey.200', borderRadius: 2, textAlign: 'center' }}><QuestionAnswer sx={{ fontSize: 36, color: 'grey.300', display: 'block', mx: 'auto', mb: 1 }} /><MDTypography variant="caption" color="text">No FAQs yet.</MDTypography></Box> : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {faqs.map((faq, idx) => (
                    <Box key={faq.id} sx={{ p: 1.75, border: '1px solid', borderRadius: 2, borderColor: editingFaq?.id === faq.id ? 'info.main' : 'grey.200', bgcolor: editingFaq?.id === faq.id ? 'info.50' : 'transparent' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, mr: 1 }}><MDTypography variant="caption" fontWeight="bold" display="block">Q{idx + 1}. {faq.question}</MDTypography><MDTypography variant="caption" color="text" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', opacity: 0.65, mt: 0.25 }}>{faq.answer}</MDTypography></Box>
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                          {canUpdate && (<Tooltip title="Edit"><IconButton size="small" color="info" onClick={() => handleEditFaq(faq)}><Edit fontSize="small" /></IconButton></Tooltip>)}
                          {canDelete && (<Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteFaq(faq.id)}><Delete fontSize="small" /></IconButton></Tooltip>)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
            {(editingFaq ? canUpdate : canCreate) && (
              <Grid item xs={12} md={5}>
                <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={2}>{editingFaq ? '✏️ Edit FAQ' : '+ Create FAQ'}</MDTypography>
                  <form onSubmit={handleSubmitFaq(handleFaqSubmit)}>
                    <TextField label="Question *" fullWidth sx={{ mb: 2 }} {...registerFaq('faqQuestion', { required: true })} />
                    <TextField label="Answer *" fullWidth multiline rows={4} sx={{ mb: 2 }} {...registerFaq('faqAnswer', { required: true })} />
                    <TextField label="Order" type="number" fullWidth size="small" sx={{ mb: 2.5 }} defaultValue={0} inputProps={{ min: 0 }} {...registerFaq('faqOrder')} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {editingFaq && <MDButton fullWidth size="small" color="secondary" onClick={() => { setEditingFaq(null); resetFaq(); }}>Cancel</MDButton>}
                      <MDButton fullWidth type="submit" color="info" variant="contained">{editingFaq ? 'Update' : 'Create FAQ'}</MDButton>
                    </Box>
                  </form>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><MDButton onClick={closeFaqDialog} variant="outlined">Done</MDButton></DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default CategoryDesigns;
